import { useState, useMemo, useCallback } from 'react'
import { LayoutGrid, List as ListIcon } from 'lucide-react'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import { getContractProcesses } from '../../services'
import { ContractProcess } from '../../types'
import ContractProcessButtons from '../../shared/Buttons'
import { getContractCustomer } from '@/modules/Contract/services/customer'
import ProcessTimeline from './ProcessTimeline'
import ProcessDetailDrawer from './ProcessDetailDrawer'
import { useProcessColumns } from './use-process-columns'
import { useProcessActions } from './use-process-actions'

interface ProcessListContentProps {
    contractID: number
}

type ViewMode = 'table' | 'timeline'

/** Embeddable process list — table/timeline toggle, accepts contractID as prop */
const ProcessListContent = ({ contractID }: ProcessListContentProps) => {
    const { t } = useTranslation()
    const [viewMode, setViewMode] = useState<ViewMode>('table')
    const [searchParams, setSearchParams] = useSearchParams()

    const { columns, attachmentExpandable } = useProcessColumns()
    const { actions } = useProcessActions()

    const { data } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACT, contractID],
            queryFn: () => getContractCustomer({ contractID }),
        },
        { showLoading: false, showError: false }
    )

    const { data: processes, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACTS_PROCESSES, contractID],
        queryFn: () => getContractProcesses({ contractID }),
    })

    const shouldCheckRegistrationDate =
        !data?.result?.registration_date &&
        ['exchanging', 'pending', 'uploading'].includes(
            data?.result?.contract_status || ''
        )

    // URL-driven process selection for deep-linking & browser history
    const processIdFromUrl = searchParams.get('process_id')
    const selectedProcessId = processIdFromUrl ? Number(processIdFromUrl) : null

    const selectedProcess = useMemo(() => {
        if (!selectedProcessId || !processes) return null
        return processes.find(
            (p) => p.contract_process_id === selectedProcessId
        )
    }, [selectedProcessId, processes])

    const handleSelectProcess = useCallback(
        (row: ContractProcess) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('process_id', String(row.contract_process_id))
                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const handleDrawerClose = useCallback(() => {
        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.delete('process_id')
                return newParams
            },
            { replace: true }
        )
    }, [setSearchParams])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper className="mt-2">
            <div className="flex items-center justify-end mb-2">
                <div className="flex items-center gap-2">
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('table')}
                        className={
                            viewMode === 'table'
                                ? 'text-primary'
                                : 'text-gray-400'
                        }
                    >
                        <ListIcon className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setViewMode('timeline')}
                        className={
                            viewMode === 'timeline'
                                ? 'text-primary'
                                : 'text-gray-400'
                        }
                    >
                        <LayoutGrid className="w-4 h-4" />
                    </IconButton>
                    <ContractProcessButtons.Create contractID={contractID} />
                </div>
            </div>

            {viewMode === 'table' ? (
                <DataTableCustomized<ContractProcess>
                    columns={columns}
                    rows={processes || []}
                    rowKey={(row) => String(row.contract_process_id)}
                    size="small"
                    resizable
                    actions={actions}
                    expandable={attachmentExpandable}
                    onRowClick={handleSelectProcess}
                    emptyMessage={t('contract-process-empty')}
                    toolbar={{
                        settings: {
                            show: false,
                            storageKey: 'contract-processes',
                        },
                    }}
                />
            ) : (
                <>
                    {processes && processes.length > 0 ? (
                        <ProcessTimeline
                            processes={processes}
                            selectedProcessId={selectedProcessId}
                            onSelect={handleSelectProcess}
                        />
                    ) : (
                        <div className="h-20 flex justify-center items-center">
                            <Typogaphy className="text-gray-500">
                                {t('contract-process-empty')}
                            </Typogaphy>
                        </div>
                    )}
                </>
            )}

            <ProcessDetailDrawer
                process={selectedProcess}
                contractID={contractID}
                shouldCheckRegistrationDate={shouldCheckRegistrationDate}
                onClose={handleDrawerClose}
            />
        </Paper>
    )
}

/** Full page version — reads contractID from URL params, includes breadcrumbs */
const List = () => {
    const { contractID } = useParams()
    const { t } = useTranslation()

    const { data } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACT, contractID],
            queryFn: () =>
                getContractCustomer({ contractID: Number(contractID) }),
        },
        { showLoading: false, showError: false }
    )

    return (
        <>
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.customerContractList,
                        title: t('contract-list'),
                    },
                    {
                        path: generatePathDatacentral(
                            paths.customerContractDetail,
                            { id: contractID }
                        ),
                        title: data?.result?.contract_number_id || '---',
                    },
                    {
                        path: '',
                        title: t('contract-process-list'),
                    },
                ]}
            />
            <ProcessListContent contractID={Number(contractID)} />
        </>
    )
}

export { ProcessListContent }
export default List
